import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/en-gb';
import i18n from '.';
import axiosInstance from '../variables/api/axiosInstance';
import { formatMontant } from '../variables/constants';

/* Documents adressés à un partenaire (confirmation de garantie, attestation,
   lettre de relance) : leur langue est celle du DESTINATAIRE, pas celle de
   l'interface. Un agent qui travaille en français écrit en anglais au bureau du
   Ghana ; un agent en anglais écrit en français au bureau du Bénin.

   La langue vient du serveur (GET /v1/organismes/{id}/langue-documents), calculée
   comme pour les courriels qu'il envoie : celle de l'organisme, sinon de son pays,
   sinon le français. Sans destinataire, ou si la lecture échoue, le français :
   c'est le document d'avant. */

const cache = new Map();

/** 'fr' ou 'en', une seule lecture par organisme et par chargement de page. */
export function langueDocument(organismeTrackingId) {
  if (!organismeTrackingId) return Promise.resolve('fr');
  if (!cache.has(organismeTrackingId)) {
    cache.set(organismeTrackingId, axiosInstance
      .get(`/v1/organismes/${organismeTrackingId}/langue-documents`, { skipErrorToast: true })
      .then((r) => (r.data?.data === 'EN' ? 'en' : 'fr'))
      .catch(() => {
        cache.delete(organismeTrackingId); // réessayer au prochain affichage
        return 'fr';
      }));
  }
  return cache.get(organismeTrackingId);
}

/** { langue, pret } : 'fr' tant que la réponse n'est pas arrivée. */
export function useLangueDocument(organismeTrackingId) {
  const [etat, setEtat] = useState({ langue: 'fr', pret: !organismeTrackingId });
  useEffect(() => {
    let actif = true;
    setEtat({ langue: 'fr', pret: !organismeTrackingId });
    langueDocument(organismeTrackingId).then((langue) => {
      if (actif) setEtat({ langue, pret: true });
    });
    return () => { actif = false; };
  }, [organismeTrackingId]);
  return etat;
}

/** Langue des documents remis à une victime (offre, note, quittance) : celle de sa fiche,
    le français si elle n'en porte pas. */
export const langueVictime = (victime) => (victime?.langue === 'EN' ? 'en' : 'fr');

/** t() du contenu d'un document, figé dans la langue du destinataire. */
export const tDocument = (langue) => i18n.getFixedT(langue === 'en' ? 'en' : 'fr', 'documents');

/** Date d'un document dans sa langue, sans toucher à la langue globale de dayjs. */
export const dateDocument = (d, format, langue) => dayjs(d).locale(langue === 'en' ? 'en-gb' : 'fr').format(format);

/**
 * `formatDate` de variables/constants, mais dans la langue du DOCUMENT : « Le 31/05/2026 »
 * (« … à 14:30 » pour une date avec heure) en français, comme avant ; « 31/05/2026 »
 * (« …, 14:30 ») en anglais. Celui de l'écran suit la langue de l'agent.
 */
export function formatDateDocument(d, langue) {
  if (!d) return '-';
  const locale = langue === 'en' ? 'en-GB' : 'fr-FR';
  const dt = new Date(d);
  const date = dt.toLocaleDateString(locale);
  const t = tDocument(langue);
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return t('dates.le', { date });
  return t('dates.leA', { date, heure: dt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) });
}

/**
 * Montant d'un document : `formatMontant` en français, inchangé (« 1 250 000 FCFA ») ;
 * séparateurs anglais en anglais (« 1,250,000 FCFA »).
 */
export function formatMontantDocument(v, langue) {
  if (langue !== 'en') return formatMontant(v);
  if (v == null) return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  const decimales = !Number.isInteger(n);
  const chiffres = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: decimales ? 2 : 0, maximumFractionDigits: 2,
  }).format(n);
  return `${chiffres} FCFA`;
}

const UNITES_EN = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const DIZAINES_EN = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

const moinsDeCentEn = (n) => (n < 20
  ? UNITES_EN[n]
  : DIZAINES_EN[Math.floor(n / 10)] + (n % 10 ? `-${UNITES_EN[n % 10]}` : ''));

const moinsDeMilleEn = (n) => {
  const centaines = Math.floor(n / 100);
  const reste = n % 100;
  if (!centaines) return moinsDeCentEn(reste);
  return `${UNITES_EN[centaines]} hundred${reste ? ` and ${moinsDeCentEn(reste)}` : ''}`;
};

/**
 * Montant en toutes lettres, en anglais britannique, partie entière seulement comme
 * la version française de la quittance : 1 201 001 081 → « One billion, two hundred and
 * one million, one thousand and eighty-one CFA francs ».
 */
export function montantEnLettresAnglais(valeur) {
  const n = Math.trunc(Math.abs(Number(valeur) || 0));
  if (n === 0) return 'Zero CFA francs';
  const groupes = [];
  let reste = n;
  [[1e9, 'billion'], [1e6, 'million'], [1e3, 'thousand']].forEach(([echelle, nom]) => {
    const q = Math.floor(reste / echelle);
    if (q) groupes.push(`${moinsDeMilleEn(q)} ${nom}`);
    reste %= echelle;
  });
  let texte = groupes.join(', ');
  // Le dernier groupe sous la centaine s'accroche par « and » : « one thousand and five ».
  if (reste) texte += groupes.length ? `${reste < 100 ? ' and ' : ', '}${moinsDeMilleEn(reste)}` : moinsDeMilleEn(reste);
  return `${texte.charAt(0).toUpperCase()}${texte.slice(1)} CFA franc${n > 1 ? 's' : ''}`;
}

/** Pour les tests. */
export const viderCacheLangues = () => cache.clear();
